import { useCallback, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { attachmentSignedUrlQuery, useResolvedAttachableModel } from 'hooks/useAttachmentSignedUrl';
import { useToastActions } from 'stores/toastStore';

/**
 * Returns a click handler that opens an attachment in a new tab.
 *
 * Signed URLs expire ~30s after the API issues them, so the handler always
 * fetches a fresh URL on click — never reusing the query cache — and
 * navigates a tab opened synchronously within the click gesture, which keeps
 * popup blockers happy while the fetch is in flight.
 */
export function useClickAttachment(attachmentId: string, attachableModel?: string, page?: number) {
  const queryClient = useQueryClient();
  const resolvedAttachableModel = useResolvedAttachableModel(attachableModel);
  const { addToast } = useToastActions();
  const openInFlight = useRef(false);

  return useCallback(async () => {
    // Without an id the request would fall through to the attachments index
    // endpoint; the pre-rewrite hook treated a blank id as a no-op, keep that.
    if (!attachmentId || openInFlight.current) return;
    openInFlight.current = true;

    let win: Window | null = null;
    try {
      win = window.open('', '_blank');
      if (win) {
        // Sever the tab's back-reference before it navigates to the
        // (cross-origin, user-uploaded) document; our own handle still works.
        win.opener = null;
        if (win.document?.body) {
          win.document.body.textContent = 'Opening document…';
        }
      }

      const attachment = await queryClient.fetchQuery({
        ...attachmentSignedUrlQuery(attachmentId, resolvedAttachableModel),
        staleTime: 0,
        // Fail fast when offline instead of stranding the placeholder tab on
        // a paused fetch that resumes (and navigates) minutes later.
        networkMode: 'always',
      });
      if (!attachment?.url) {
        throw new Error(`No URL returned for attachment ${attachmentId}`);
      }

      const target = typeof page === 'number' ? `${attachment.url}#page=${page}` : attachment.url;
      if (win) {
        win.location.replace(target);
      } else if (!window.open(target, '_blank')) {
        throw new Error('Popup blocked');
      }
    } catch {
      win?.close();
      addToast({ text: 'Sorry, we couldn’t open that document. Please try again.' });
    } finally {
      openInFlight.current = false;
    }
  }, [attachmentId, resolvedAttachableModel, page, queryClient, addToast]);
}
